import { createContext, useEffect } from 'react'
import { useQuery } from 'react-query'

import { generateDates } from 'features/dashboards-v2-1/features/filter-header/utils/date-utils'
import {
  fetchFilterOptionsV2,
  useFilterService,
} from 'features/filters/service'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import type { FilterOption } from 'features/filters/types'
import { HardcodedFilterOptionsEnum } from 'features/filters/utils'
import { useProjectStore } from 'features/project/store'
import { ProjectState } from 'types/project'

/** The hub's default filter options query, keyed by hub. */
export const projectFilterListKey = (projectId: string) =>
  ['filter-list-data', projectId, [], null] as const

/**
 * The hub whose options query an ancestor always runs (the project root), or
 * null. A reader under it sees the query idle until the root's effect starts
 * it; elsewhere (shared dashboards) an idle query may never run.
 */
export const ProjectFilterListOwnerContext = createContext<string | null>(null)

export const useProjectFilterList = (
  projectId: string,
): { filterList: FilterOption[] | undefined; isLoading: boolean } => {
  const filterService = useFilterService()
  const setDefaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.setDefaultFilterList,
  )
  const setDateWindows = useProjectStore(
    (state: ProjectState) => state.setDateWindows,
  )

  const { data: filterList, isLoading } = useQuery(
    projectFilterListKey(projectId),
    () =>
      fetchFilterOptionsV2(projectId, null, filterService, {
        fields: [HardcodedFilterOptionsEnum.CREATE_TIME],
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: !!projectId,
    },
  )

  useEffect(() => {
    if (filterList) {
      setDefaultFilterList(filterList)
      setDateWindows(generateDates(filterList))
    }
  }, [filterList, setDateWindows, setDefaultFilterList])

  return { filterList, isLoading }
}
